// Чистая логика рабочего места логиста (без Vue и сети) — покрыта unit-тестами.

export const LEVEL_LABELS = {
  critical: 'Горит',
  warning: 'Внимание',
  info: 'Справка',
}

export const STATE_LABELS = {
  ok: 'Работает',
  degraded: 'Есть задержки',
  down: 'Сбой',
}

function plural(n, one, few, many) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

export function humanDuration(totalMinutes) {
  const m = Math.abs(Math.round(totalMinutes))
  const h = Math.floor(m / 60)
  const rest = m % 60
  if (h >= 48) {
    const d = Math.floor(h / 24)
    return `${d} ${plural(d, 'день', 'дня', 'дней')}`
  }
  if (h === 0) return `${rest} мин`
  return rest ? `${h} ч ${rest} мин` : `${h} ч`
}

// «через 2 ч 30 мин» / «1 ч назад» / «сейчас»
export function relativeToNow(minutes) {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return ''
  if (Math.abs(minutes) < 5) return 'сейчас'
  return minutes > 0 ? `через ${humanDuration(minutes)}` : `${humanDuration(minutes)} назад`
}

// '2026-11-02T14:30' → '02.11 14:30' (время склада, как пришло из TMS)
export function formatLocal(iso) {
  if (!iso) return '—'
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso)
  if (!match) return iso
  const [, , mm, dd, hh, mi] = match
  return `${dd}.${mm} ${hh}:${mi}`
}

export function route(trip) {
  const from = trip?.city_out || '—'
  const to = trip?.city_in || '—'
  return `${from} → ${to}`
}

// Карточки, которые сразу раскрыты: всё «горящее», где есть рейсы.
export function initiallyOpen(groups) {
  return new Set((groups || []).filter((g) => g.level === 'critical' && g.count > 0).map((g) => g.key))
}

export function urgencyClass(trip) {
  const m = trip?.load_in_minutes
  if (m === null || m === undefined) return ''
  if (m < 0) return 'is-overdue'
  if (m <= 6 * 60) return 'is-soon'
  return ''
}

// Текст обращения в IT: всё, что нужно, чтобы разобраться без переписки.
export function buildItReport({ comment, page, user, health, trip, errors, now, userAgent }) {
  const lines = []
  lines.push('Обращение в IT из ERP')
  lines.push(`Когда: ${now || new Date().toLocaleString('ru-RU')}`)
  if (user) lines.push(`Кто: ${user}`)
  if (page) lines.push(`Страница: ${page}`)
  if (trip) lines.push(`Рейс: ${trip}`)
  lines.push('')
  lines.push(`Что случилось: ${(comment || '').trim() || '(не указано)'}`)
  if (health?.overall) {
    lines.push('')
    lines.push(`Состояние системы: ${STATE_LABELS[health.overall.state] || health.overall.state} — ${health.overall.text}`)
    for (const check of health.checks || []) {
      if (check.state !== 'ok') lines.push(`  • ${check.title}: ${check.text}`)
    }
  }
  const recent = (errors || []).slice(0, 5)
  if (recent.length) {
    lines.push('')
    lines.push('Последние ошибки в браузере:')
    for (const e of recent) {
      const where = [e.method, e.url].filter(Boolean).join(' ')
      const code = e.status ? ` [${e.status}]` : ''
      lines.push(`  • ${e.time} ${where}${code}: ${e.technical || e.message}`)
    }
  }
  if (userAgent) {
    lines.push('')
    lines.push(`Браузер: ${userAgent}`)
  }
  return lines.join('\n')
}

// ---------------------------------------------------------------- срок данных и площадки

// Срок внесения реальных данных: «до 01.11 12:00» / «срок прошёл 3 ч назад».
export function deadlineText(trip) {
  if (!trip?.deadline_at) return ''
  const when = formatLocal(trip.deadline_at)
  const m = trip.deadline_in_minutes
  if (m === null || m === undefined) return `данные до ${when}`
  if (m < 0) return `срок данных ${when} прошёл`
  return `данные до ${when} (${relativeToNow(m)})`
}

export function filterBySite(trips, site) {
  if (!site) return trips || []
  return (trips || []).filter((t) => (t.site || 'Площадка не указана') === site)
}

// ---------------------------------------------------------------- показатели группы

const RUB = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })

export function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2).replace('.', ',')} млн ₽`
  if (abs >= 10_000) return `${RUB.format(Math.round(value / 1000))} тыс. ₽`
  return `${RUB.format(Math.round(value))} ₽`
}

export function formatGoalValue(unit, value) {
  if (value === null || value === undefined) return '—'
  if (unit === 'rub') return formatMoney(value)
  if (unit === 'pct') return `${String(value).replace('.', ',')} %`
  return RUB.format(value)
}

export function goalTargetText(card) {
  if (!card) return ''
  if (card.direction === 'not_negative') return 'цель: не ниже 0'
  if (card.target === null || card.target === undefined) return ''
  const target = formatGoalValue(card.unit, card.target)
  if (card.direction === 'lower') return `цель: не больше ${target}`
  return `цель: от ${target}`
}

export const LIGHT_LABELS = {
  green: 'В плане',
  yellow: 'Под угрозой',
  red: 'Отстаём',
  grey: 'Нет данных',
}


// Гипотеза «кто назначал водителя» (erp-backend src/workspace/hypothesis.py).
export function hypothesisText(h) {
  if (!h || !h.name) return ''
  const basis = h.source === 'responsible' ? 'по прошлым рейсам с этим водителем' : 'по журналу назначений'
  return `Предположительно: ${h.name} (${h.hits} из ${h.of}, ${basis})`
}
